import { motion } from "framer-motion";
import { ArrowRight, Sparkles, TrendingUp, Users, Target, Award, CheckCircle2, Zap, Mail } from "lucide-react";
import { SiWhatsapp, SiInstagram, SiLinkedin } from 'react-icons/si';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { IMAGES } from "@/assets/images";
import {
  SECTIONS,
  FORMATIONS_DATA,
  PROBLEMATIQUES,
  BENEFICES,
  CIBLES,
  CREDIBILITE_POINTS,
  METHODE_POINTS,
  CONTACT_INFO
} from "@/lib/index";

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true, margin: "-100px" }
};

const staggerItem = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

export default function Home() {
  return (
    <div className="w-full">
      <section id={SECTIONS.HERO} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HERO_BG_7}
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Formation IA & Pilotage de Salon</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-6 bg-gradient-to-b from-foreground to-foreground/70 bg-clip-text text-transparent"
            >
              Deux compétences
              <br />
              <span className="text-primary">deviennent essentielles</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed"
            >
              IA pour les coiffeurs. Pilotage pour les dirigeants.
              <br />
              Le salon de demain se construit aujourd'hui.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-2xl group"
                asChild
              >
                <a 
                  href={`#${SECTIONS.FORMATIONS}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById(SECTIONS.FORMATIONS);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  Découvrir les formations
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-2xl border-2"
                asChild
              >
                <a 
                  href={`#${SECTIONS.CONTACT}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById(SECTIONS.CONTACT);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                >
                  Prendre contact
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-6 h-10 rounded-full border-2 border-primary/30 flex items-start justify-center p-2"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-1.5 h-1.5 rounded-full bg-primary"
            />
          </motion.div>
        </div>
      </section>

      <section id={SECTIONS.INTRO} className="py-24 md:py-32 relative overflow-hidden bg-secondary">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary via-secondary/95 to-secondary/90" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div {...fadeInUp} className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-4xl md:text-6xl font-bold mb-8 text-white">
                  Loris Hug
                </h2>
                <div className="space-y-6 text-lg md:text-xl text-white/80 leading-relaxed">
                  <p>
                    Expert en formation pour les professionnels de la coiffure, je vous accompagne avec une approche <span className="text-white font-semibold">concrète, moderne et orientée terrain</span>.
                  </p>
                  <p>
                    Mon positionnement repose sur deux expertises fortes : <span className="text-primary font-semibold">l'IA appliquée au métier de coiffeur</span> et <span className="text-white font-semibold">le pilotage de salon avec l'appui de l'IA</span>.
                  </p>
                  <p>
                    Je transmets des compétences qui font la différence : <span className="text-white font-semibold">expertise, modernité, crédibilité, vision et puissance</span>.
                  </p>
                </div>
              </div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="order-1 lg:order-2"
              >
                <div className="relative">
                  <div className="aspect-square rounded-3xl overflow-hidden border-4 border-white/20 shadow-2xl shadow-black/20">
                    <img
                      src={IMAGES.IMG_8BD5B8E9869D8B227177D795851F16E0_EXIF_49}
                      alt="Loris Hug"
                      className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-br from-primary to-white/30 rounded-2xl -z-10 blur-2xl opacity-50" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-32 md:py-40 relative overflow-hidden bg-gradient-to-br from-primary via-primary/95 to-primary/90">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.05),transparent_50%)]" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-5xl mx-auto"
          >
            <div className="text-center mb-12">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 mb-8"
              >
                <Target className="w-5 h-5 text-white" />
                <span className="text-sm font-semibold text-white uppercase tracking-wider">Ma Vision & Promesse</span>
              </motion.div>
              
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-black mb-8 text-white leading-tight">
                Mon pourquoi
              </h2>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl">
              <div className="space-y-6 text-lg md:text-xl text-white/90 leading-relaxed">
                <p className="text-2xl md:text-3xl font-bold text-white">
                  Éviter aux coiffeurs de vivre ce que j'ai vu et vécu.
                </p>
                
                <p>
                  Des heures, de la pression, des responsabilités… <span className="text-white font-semibold">sans jamais avoir les bons outils</span> pour comprendre vraiment son activité.
                </p>
                
                <p className="text-xl md:text-2xl font-semibold text-white border-l-4 border-white/30 pl-6 py-2">
                  J'ai compris une chose : ce n'est pas le manque de travail le problème, c'est le manque de pilotage.
                </p>
                
                <div className="pt-6 border-t border-white/20">
                  <p className="text-xl md:text-2xl font-bold text-white mb-4">
                    Aujourd'hui, ma mission est claire :
                  </p>
                  <p>
                    Aider les coiffeurs à <span className="text-white font-semibold">reprendre le contrôle</span>, avec des outils simples, concrets, et adaptés à leur réalité — dont l'IA.
                  </p>
                </div>
                
                <div className="bg-white/10 rounded-2xl p-6 md:p-8 border border-white/20">
                  <p className="text-2xl md:text-3xl font-black text-white text-center">
                    Pour qu'ils puissent enfin travailler avec <span className="text-white">sérénité</span>…<br />
                    et non plus en <span className="line-through opacity-60">survie</span>.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id={SECTIONS.PROBLEMATIQUES} className="py-24 md:py-32">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Les vraies difficultés des salons
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Des problématiques concrètes qui freinent votre développement
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {PROBLEMATIQUES.map((probleme) => (
              <motion.div key={probleme.id} variants={staggerItem}>
                <Card className="p-8 h-full bg-card/50 backdrop-blur border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10">
                  <h3 className="text-2xl font-bold mb-4 text-primary">
                    {probleme.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {probleme.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id={SECTIONS.FORMATIONS} className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div {...fadeInUp} className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Choisissez votre formation
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Deux parcours distincts pour deux besoins essentiels
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {FORMATIONS_DATA.map((formation, index) => (
              <motion.div
                key={formation.id}
                initial={{ opacity: 0, x: index === 0 ? -60 : 60 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: index * 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <Card className={`p-10 h-full bg-gradient-to-br ${formation.gradient} border-2 ${index === 0 ? 'border-primary/20 hover:border-primary/40 hover:shadow-primary/20' : 'border-secondary/20 hover:border-secondary/40 hover:shadow-secondary/20'} transition-all duration-500 hover:shadow-2xl hover:scale-[1.02]`}>
                  <div className="mb-8">
                    <div className="w-full h-64 rounded-2xl overflow-hidden mb-6">
                      <img
                        src={index === 0 ? IMAGES.AI_TECH_2 : IMAGES.ANALYTICS_1}
                        alt={formation.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-3xl md:text-4xl font-black mb-4">
                      {formation.title}
                    </h3>
                    <p className={`text-xl font-semibold mb-4 ${index === 0 ? 'text-primary' : 'text-secondary'}`}>
                      {formation.subtitle}
                    </p>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      {formation.description}
                    </p>
                  </div>

                  <div className="space-y-4 mb-8">
                    {formation.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle2 className={`w-6 h-6 flex-shrink-0 mt-0.5 ${index === 0 ? 'text-primary' : 'text-secondary'}`} />
                        <span className="text-base leading-relaxed">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    size="lg"
                    className={`w-full text-lg py-6 rounded-xl group ${index === 0 ? 'bg-primary hover:bg-primary/90' : 'bg-secondary hover:bg-secondary/90'}`}
                    asChild
                  >
                    <a 
                      href={formation.ctaLink}
                      onClick={(e) => {
                        e.preventDefault();
                        const targetId = formation.ctaLink.replace('#', '');
                        const element = document.getElementById(targetId);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                    >
                      {formation.ctaText}
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id={SECTIONS.POUR_QUI} className="py-24 md:py-32">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Pour qui ?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Ces formations s'adressent à tous les professionnels de la coiffure
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {CIBLES.map((cible, index) => (
              <motion.div key={index} variants={staggerItem}>
                <Card className={`p-6 text-center bg-card/50 backdrop-blur border-border/50 transition-all duration-300 hover:shadow-lg ${index % 2 === 0 ? 'hover:border-primary/50 hover:shadow-primary/10' : 'hover:border-secondary/50 hover:shadow-secondary/10'}`}>
                  <Users className={`w-12 h-12 mx-auto mb-4 ${index % 2 === 0 ? 'text-primary' : 'text-secondary'}`} />
                  <p className="text-lg font-semibold">{cible}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id={SECTIONS.BENEFICES} className="py-24 md:py-32 relative overflow-hidden bg-secondary">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary via-secondary/95 to-secondary/90" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white">
              Ce que ces formations changent
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Une transformation concrète de votre pratique professionnelle
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <Card className="p-10 bg-background/95 border-destructive/30 text-foreground">
                <h3 className="text-3xl font-bold mb-6 text-destructive">Avant</h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-destructive text-2xl">×</span>
                    <span className="text-lg">Débordé par les tâches quotidiennes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-destructive text-2xl">×</span>
                    <span className="text-lg">Pilotage à l'aveugle</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-destructive text-2xl">×</span>
                    <span className="text-lg">Communication peu structurée</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-destructive text-2xl">×</span>
                    <span className="text-lg">Retard sur les outils modernes</span>
                  </li>
                </ul>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <Card className="p-10 bg-background/95 border-primary/30 text-foreground">
                <h3 className="text-3xl font-bold mb-6 text-primary">Après</h3>
                <ul className="space-y-4">
                  {BENEFICES.map((benefice, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-lg">{benefice}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      <section id={SECTIONS.CREDIBILITE} className="py-24 md:py-32 bg-secondary">
        <div className="container mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white">
              Pourquoi me faire confiance ?
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Une expertise reconnue au service des professionnels de la coiffure
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-1"
            >
              <div className="sticky top-32">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border-2 border-white/20 shadow-xl shadow-black/20">
                  <img
                    src={IMAGES.IMG_453BC1034380A9E85AFD285EC94B18D6_EXIF_48}
                    alt="Loris Hug - Formation"
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="whileInView"
              viewport={{ once: true, margin: "-100px" }}
              className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {CREDIBILITE_POINTS.map((point, index) => (
                <motion.div key={index} variants={staggerItem}>
                  <Card className="p-8 h-full bg-background/95 backdrop-blur border-primary/30 hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 text-foreground">
                    <Award className="w-12 h-12 text-primary mb-4" />
                    <h3 className="text-2xl font-bold mb-3">{point.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {point.description}
                    </p>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section id={SECTIONS.METHODE} className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-6xl font-bold mb-6">
                Une approche concrète
              </h2>
              <p className="text-xl text-muted-foreground">
                Des formations orientées action, pas de théorie abstraite
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {METHODE_POINTS.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="flex items-start gap-4 p-6 rounded-2xl bg-card/30 border border-border/50"
                >
                  <Target className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <p className="text-lg leading-relaxed">{point}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-gradient-to-b from-background via-primary/5 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <div className="aspect-[3/4] rounded-2xl overflow-hidden border-2 border-primary/20 shadow-xl">
              <img
                src={IMAGES.IMG_08015818F0190DE0CD83B57E52F0494F_EXIF_47}
                alt="Loris Hug - Expertise"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
            <div className="aspect-[3/4] rounded-2xl overflow-hidden border-2 border-secondary/20 shadow-xl">
              <img
                src={IMAGES.IMG_5275_3_50}
                alt="Loris Hug - Formation"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
            <div className="aspect-[3/4] rounded-2xl overflow-hidden border-2 border-primary/20 shadow-xl">
              <img
                src={IMAGES.IMG_8BD5B8E9869D8B227177D795851F16E0_EXIF_49}
                alt="Loris Hug - Accompagnement"
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section id={SECTIONS.CONTACT} className="py-32 md:py-40 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.SALON_LUXURY_10}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-6xl mx-auto"
          >
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-7xl font-black mb-8">
                Passez un cap
              </h2>
              <p className="text-2xl md:text-3xl text-muted-foreground mb-4 leading-relaxed">
                Le plus grand risque aujourd'hui,
                <br />
                <span className="text-foreground font-semibold">c'est de continuer comme avant</span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card className="p-8 h-full bg-gradient-to-br from-primary/10 via-background to-background border-2 border-primary/20 hover:border-primary/40 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 bg-primary/10 rounded-xl">
                      <Sparkles className="w-8 h-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Formation IA pour Coiffeurs</h3>
                      <p className="text-muted-foreground">Maîtrisez l'intelligence artificielle au quotidien</p>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="w-full text-lg py-6 rounded-xl group bg-primary hover:bg-primary/90"
                    asChild
                  >
                    <a
                      href={CONTACT_INFO.whatsappFormationIA}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SiWhatsapp className="mr-2 w-5 h-5" />
                      Me contacter pour l'IA
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Card className="p-8 h-full bg-gradient-to-br from-secondary/10 via-background to-background border-2 border-secondary/20 hover:border-secondary/40 transition-all duration-300 hover:shadow-xl hover:shadow-secondary/10">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="p-3 bg-secondary/10 rounded-xl">
                      <TrendingUp className="w-8 h-8 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-2">Formation Pilotage avec IA</h3>
                      <p className="text-muted-foreground">Dirigez votre salon avec clarté et performance</p>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="w-full text-lg py-6 rounded-xl group bg-secondary hover:bg-secondary/90"
                    asChild
                  >
                    <a
                      href={CONTACT_INFO.whatsappFormationPilotage}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SiWhatsapp className="mr-2 w-5 h-5" />
                      Me contacter pour le Pilotage
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                </Card>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center"
            >
              <Card className="p-10 bg-card/50 backdrop-blur border-border/50">
                <h3 className="text-2xl font-bold mb-6">Tous mes liens de contact</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <Button
                    variant="outline"
                    size="lg"
                    className="h-auto py-4 flex-col gap-2"
                    asChild
                  >
                    <a href={`mailto:${CONTACT_INFO.email}`}>
                      <Mail className="w-6 h-6" />
                      <span className="text-sm font-semibold">Email</span>
                      <span className="text-xs text-muted-foreground">{CONTACT_INFO.email}</span>
                    </a>
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="h-auto py-4 flex-col gap-2"
                    asChild
                  >
                    <a
                      href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/[\s\+]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SiWhatsapp className="w-6 h-6" />
                      <span className="text-sm font-semibold">WhatsApp</span>
                      <span className="text-xs text-muted-foreground">Message direct</span>
                    </a>
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="h-auto py-4 flex-col gap-2"
                    asChild
                  >
                    <a
                      href={`https://instagram.com/${CONTACT_INFO.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SiInstagram className="w-6 h-6" />
                      <span className="text-sm font-semibold">Instagram</span>
                      <span className="text-xs text-muted-foreground">@{CONTACT_INFO.instagram}</span>
                    </a>
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="h-auto py-4 flex-col gap-2"
                    asChild
                  >
                    <a
                      href={`https://linkedin.com/in/${CONTACT_INFO.linkedin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SiLinkedin className="w-6 h-6" />
                      <span className="text-sm font-semibold">LinkedIn</span>
                      <span className="text-xs text-muted-foreground">Profil pro</span>
                    </a>
                  </Button>
                </div>

                <p className="text-muted-foreground">
                  Choisissez le moyen de contact qui vous convient le mieux. Je réponds rapidement à toutes les demandes.
                </p>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}